import { useState, useEffect, useRef } from "react";
import { AdminLayout } from "@/components/AdminLayout";
import { useAuth } from "@/context/AuthContext";
import { locationAPI, subLocationAPI } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, MapPin, Loader2, Home, Upload, Download, FileSpreadsheet, AlertCircle, CheckCircle } from "lucide-react";
import axios from "axios";

const API_URL = process.env.REACT_APP_BACKEND_URL;

const AdminSubLocations = () => {
  const { token } = useAuth();
  const [locations, setLocations] = useState([]);
  const [subLocations, setSubLocations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingSubLocation, setEditingSubLocation] = useState(null);
  const [saving, setSaving] = useState(false);
  const [filterLocationId, setFilterLocationId] = useState("");
  
  // Bulk upload state
  const [showBulkUpload, setShowBulkUpload] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadResult, setUploadResult] = useState(null);
  const fileInputRef = useRef(null);

  const [form, setForm] = useState({
    name: "",
    location_id: "",
    slug: "",
    description: "",
    image: "",
  });

  useEffect(() => {
    fetchLocations();
    fetchSubLocations();
  }, []);

  const fetchLocations = async () => {
    try {
      const response = await locationAPI.getAll();
      setLocations(response.data);
    } catch (error) {
      toast.error("Failed to fetch locations");
    }
  };

  const fetchSubLocations = async () => {
    try {
      const response = await subLocationAPI.getAll();
      setSubLocations(response.data);
    } catch (error) {
      toast.error("Failed to fetch sub-locations");
    } finally {
      setLoading(false);
    }
  };

  const generateSlug = (name) => {
    return name.toLowerCase().trim().replace(/[^\w\s-]/g, '').replace(/[\s_-]+/g, '-').replace(/^-+|-+$/g, '');
  };

  const resetForm = () => {
    setForm({
      name: "",
      location_id: filterLocationId || "",
      slug: "",
      description: "",
      image: "",
    });
    setEditingSubLocation(null);
  };

  const handleEdit = (subLocation) => {
    setEditingSubLocation(subLocation);
    setForm({
      name: subLocation.name,
      location_id: subLocation.location_id,
      slug: subLocation.slug,
      description: subLocation.description || "",
      image: subLocation.image || "",
    });
    setShowModal(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.location_id) {
      toast.error("Please fill in name and select a location");
      return;
    }

    const submitData = {
      ...form,
      slug: form.slug || generateSlug(form.name),
    };

    setSaving(true);
    try {
      if (editingSubLocation) {
        await subLocationAPI.update(editingSubLocation.id, submitData, token);
        toast.success("Village/Area updated");
      } else {
        await subLocationAPI.create(submitData, token);
        toast.success("Village/Area created");
      }
      setShowModal(false);
      resetForm();
      fetchSubLocations();
      fetchLocations(); // Refresh to update sub_location_count
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to save");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Are you sure you want to delete this village/area?")) return;
    try {
      await subLocationAPI.delete(id, token);
      toast.success("Village/Area deleted");
      fetchSubLocations();
      fetchLocations();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to delete");
    }
  };

  // Bulk upload handlers
  const handleBulkUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!file.name.endsWith('.csv')) {
      toast.error("Please upload a CSV file");
      return;
    }

    setUploading(true);
    setUploadResult(null);

    const formData = new FormData();
    formData.append('file', file);

    try {
      const response = await axios.post(`${API_URL}/api/sub-locations/bulk-upload`, formData, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'multipart/form-data',
        },
      });
      
      setUploadResult(response.data.results);
      toast.success(response.data.message);
      fetchSubLocations();
      fetchLocations();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to upload CSV");
      setUploadResult({ errors: [error.response?.data?.detail || "Upload failed"] });
    } finally {
      setUploading(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const downloadSampleCSV = () => {
    window.open(`${API_URL}/api/sub-locations/sample-csv`, '_blank');
  };

  // Filter sub-locations based on selected location
  const filteredSubLocations = filterLocationId
    ? subLocations.filter(sl => sl.location_id === filterLocationId)
    : subLocations;

  // Group sub-locations by location for display
  const groupedSubLocations = filteredSubLocations.reduce((acc, sl) => {
    const locName = sl.location_name || "Unknown";
    if (!acc[locName]) acc[locName] = [];
    acc[locName].push(sl);
    return acc;
  }, {});

  return (
    <AdminLayout>
      <div className="space-y-6" data-testid="admin-sub-locations">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-3xl font-semibold text-foreground mb-2">
              Villages & Areas
            </h1>
            <p className="text-muted-foreground">Manage sub-locations (villages/areas) within each location</p>
          </div>
          <div className="flex flex-wrap gap-2">
            <Button variant="outline" onClick={() => setShowBulkUpload(true)} data-testid="bulk-upload-villages-btn">
              <Upload className="w-4 h-4 mr-2" />
              Bulk Upload
            </Button>
            <Button onClick={() => { resetForm(); setShowModal(true); }} className="rounded-full" data-testid="add-sub-location-btn">
              <Plus className="w-4 h-4 mr-2" />Add Village/Area
            </Button>
          </div>
        </div>

        {/* Filter by Location */}
        <div className="bg-white rounded-xl p-4 shadow-soft">
          <div className="flex items-center gap-4">
            <label className="text-sm font-medium text-foreground whitespace-nowrap">Filter by Location:</label>
            <Select value={filterLocationId || "all"} onValueChange={(val) => setFilterLocationId(val === "all" ? "" : val)}>
              <SelectTrigger className="w-64" data-testid="filter-location">
                <SelectValue placeholder="All Locations" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Locations</SelectItem>
                {locations.map((loc) => (
                  <SelectItem key={loc.id} value={loc.id}>
                    {loc.name}, {loc.state}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <span className="text-sm text-muted-foreground">
              {filteredSubLocations.length} village{filteredSubLocations.length !== 1 ? "s" : ""} found
            </span>
          </div>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-20">
            <Loader2 className="w-8 h-8 animate-spin text-primary" />
          </div>
        ) : filteredSubLocations.length > 0 ? (
          <div className="space-y-6">
            {Object.entries(groupedSubLocations).map(([locationName, subLocs]) => (
              <div key={locationName} className="space-y-3">
                <h3 className="text-lg font-semibold text-foreground flex items-center gap-2">
                  <MapPin className="w-5 h-5 text-primary" />
                  {locationName}
                  <span className="text-sm font-normal text-muted-foreground">({subLocs.length})</span>
                </h3>
                <div className="grid gap-3 pl-7">
                  {subLocs.map((subLoc) => (
                    <div 
                      key={subLoc.id} 
                      className="bg-white rounded-xl p-4 shadow-soft flex items-center gap-4"
                      data-testid={`sub-location-item-${subLoc.id}`}
                    >
                      <div className="w-12 h-12 rounded-lg bg-muted flex items-center justify-center flex-shrink-0 overflow-hidden">
                        {subLoc.image ? (
                          <img src={subLoc.image} alt={subLoc.name} className="w-full h-full object-cover" />
                        ) : (
                          <Home className="w-5 h-5 text-muted-foreground" />
                        )}
                      </div>
                      <div className="flex-1 min-w-0">
                        <h4 className="font-semibold text-foreground">{subLoc.name}</h4>
                        <p className="text-xs text-muted-foreground">
                          Slug: /{subLoc.slug} • {subLoc.homestay_count || 0} homestays
                        </p>
                        {subLoc.description && (
                          <p className="text-sm text-muted-foreground mt-1 line-clamp-1">{subLoc.description}</p>
                        )}
                      </div>
                      <div className="flex gap-2">
                        <Button 
                          variant="outline" 
                          size="sm" 
                          onClick={() => handleEdit(subLoc)}
                          data-testid={`edit-sub-location-${subLoc.id}`}
                        >
                          <Pencil className="w-4 h-4" />
                        </Button>
                        <Button 
                          variant="outline" 
                          size="sm" 
                          onClick={() => handleDelete(subLoc.id)} 
                          className="text-red-500 hover:bg-red-50"
                          data-testid={`delete-sub-location-${subLoc.id}`}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="bg-white rounded-2xl p-12 shadow-soft text-center">
            <Home className="w-16 h-16 text-muted-foreground mx-auto mb-4" />
            <h3 className="text-xl font-semibold text-foreground mb-2">No Villages/Areas Yet</h3>
            <p className="text-muted-foreground mb-6">
              {filterLocationId 
                ? "No villages found for this location. Add one to get started."
                : "Add villages or areas within your locations to organize homestays better."
              }
            </p>
            <Button onClick={() => { resetForm(); setShowModal(true); }}>
              <Plus className="w-4 h-4 mr-2" />Add First Village
            </Button>
          </div>
        )}

        {/* Add/Edit Dialog */}
        <Dialog open={showModal} onOpenChange={setShowModal}>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle>
                {editingSubLocation ? "Edit Village/Area" : "Add New Village/Area"}
              </DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4" data-testid="sub-location-form">
              <div>
                <label className="text-sm font-medium mb-1 block">Parent Location *</label>
                <Select 
                  value={form.location_id} 
                  onValueChange={(value) => setForm({ ...form, location_id: value })}
                >
                  <SelectTrigger data-testid="form-parent-location">
                    <SelectValue placeholder="Select a location" />
                  </SelectTrigger>
                  <SelectContent>
                    {locations.map((loc) => (
                      <SelectItem key={loc.id} value={loc.id}>
                        {loc.name}, {loc.state}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-medium mb-1 block">Village/Area Name *</label>
                  <Input 
                    value={form.name} 
                    onChange={(e) => { 
                      setForm({ ...form, name: e.target.value }); 
                      if (!editingSubLocation) {
                        setForm(f => ({ ...f, slug: generateSlug(e.target.value) })); 
                      }
                    }} 
                    placeholder="e.g., Karde, Murud"
                    data-testid="form-sub-loc-name"
                  />
                </div>
                <div>
                  <label className="text-sm font-medium mb-1 block">Slug</label>
                  <Input 
                    value={form.slug} 
                    onChange={(e) => setForm({ ...form, slug: e.target.value })} 
                    placeholder="karde"
                    data-testid="form-sub-loc-slug"
                  />
                </div>
              </div>
              
              <div>
                <label className="text-sm font-medium mb-1 block">Description</label>
                <Textarea 
                  value={form.description} 
                  onChange={(e) => setForm({ ...form, description: e.target.value })} 
                  placeholder="Beautiful beach village..."
                  rows={2}
                />
              </div>
              
              <div>
                <label className="text-sm font-medium mb-1 block">Image URL</label>
                <Input 
                  value={form.image} 
                  onChange={(e) => setForm({ ...form, image: e.target.value })} 
                  placeholder="https://..."
                />
              </div>
              
              <div className="flex justify-end gap-3 pt-2">
                <Button type="button" variant="outline" onClick={() => setShowModal(false)}>
                  Cancel
                </Button>
                <Button type="submit" disabled={saving} data-testid="form-sub-loc-submit">
                  {saving && <Loader2 className="w-4 h-4 animate-spin mr-2" />}
                  {editingSubLocation ? "Update" : "Create"}
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>

        {/* Bulk Upload Modal */}
        <Dialog open={showBulkUpload} onOpenChange={setShowBulkUpload}>
          <DialogContent className="max-w-xl">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <FileSpreadsheet className="w-5 h-5" />
                Bulk Upload Villages/Areas
              </DialogTitle>
            </DialogHeader>
            
            <div className="space-y-6">
              {/* Instructions */}
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <h4 className="font-semibold text-blue-900 mb-2">CSV Format</h4>
                <p className="text-sm text-blue-800 mb-3">
                  Upload a CSV file with the following columns:
                </p>
                <div className="bg-white rounded p-3 font-mono text-xs overflow-x-auto">
                  <div className="text-gray-500">name,location_name,slug,description</div>
                  <div>Revdanda,Alibaug,revdanda,Historic coastal village</div>
                  <div>Kihim,Alibaug,kihim,Pristine beach destination</div>
                </div>
                <div className="mt-3 text-xs text-blue-700">
                  <p><strong>Required:</strong> name, location_name (must match existing location)</p>
                  <p><strong>Optional:</strong> slug (auto-generated from name), description</p>
                </div>
              </div>

              {/* Download Sample */}
              <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                <div>
                  <p className="font-medium text-gray-900">Download Sample CSV</p>
                  <p className="text-sm text-gray-600">Get a template with example data</p>
                </div>
                <Button variant="outline" onClick={downloadSampleCSV}>
                  <Download className="w-4 h-4 mr-2" />
                  Download
                </Button>
              </div>

              {/* Upload Area */}
              <div className="border-2 border-dashed border-gray-300 rounded-lg p-8 text-center hover:border-primary transition-colors">
                <input
                  ref={fileInputRef}
                  type="file"
                  accept=".csv"
                  onChange={handleBulkUpload}
                  className="hidden"
                  id="csv-upload-villages"
                />
                <label htmlFor="csv-upload-villages" className="cursor-pointer">
                  {uploading ? (
                    <div className="flex flex-col items-center gap-3">
                      <Loader2 className="w-10 h-10 animate-spin text-primary" />
                      <p className="text-muted-foreground">Processing CSV...</p>
                    </div>
                  ) : (
                    <div className="flex flex-col items-center gap-3">
                      <Upload className="w-10 h-10 text-muted-foreground" />
                      <div>
                        <p className="font-medium text-foreground">Click to upload CSV</p>
                        <p className="text-sm text-muted-foreground">or drag and drop</p>
                      </div>
                    </div>
                  )}
                </label>
              </div>

              {/* Upload Results */}
              {uploadResult && (
                <div className="border rounded-lg p-4 space-y-3">
                  <h4 className="font-semibold">Upload Results</h4>
                  <div className="grid grid-cols-3 gap-4 text-center">
                    <div className="p-3 bg-green-50 rounded-lg">
                      <CheckCircle className="w-5 h-5 text-green-600 mx-auto mb-1" />
                      <p className="text-2xl font-bold text-green-600">{uploadResult.created || 0}</p>
                      <p className="text-xs text-green-700">Created</p>
                    </div>
                    <div className="p-3 bg-blue-50 rounded-lg">
                      <Pencil className="w-5 h-5 text-blue-600 mx-auto mb-1" />
                      <p className="text-2xl font-bold text-blue-600">{uploadResult.updated || 0}</p>
                      <p className="text-xs text-blue-700">Updated</p>
                    </div>
                    <div className="p-3 bg-gray-50 rounded-lg">
                      <AlertCircle className="w-5 h-5 text-gray-600 mx-auto mb-1" />
                      <p className="text-2xl font-bold text-gray-600">{uploadResult.skipped || 0}</p>
                      <p className="text-xs text-gray-700">Skipped</p>
                    </div>
                  </div>
                  {uploadResult.errors?.length > 0 && (
                    <div className="mt-3 p-3 bg-red-50 rounded-lg">
                      <p className="text-sm font-medium text-red-800 mb-2">Errors:</p>
                      <ul className="text-xs text-red-700 space-y-1 max-h-32 overflow-y-auto">
                        {uploadResult.errors.map((err, i) => (
                          <li key={i}>• {err}</li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
              )}
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </AdminLayout>
  );
};

export default AdminSubLocations;
